import type { ToolResultType } from "schemas/chat";
import type {
  ChatMessageContent,
  ChatMessage as ChatMessageType,
} from "schemas/generated/chatMessage";

import React, { useState } from "react";

import { ImageOverlay } from "@editor/components/common/designSystem/ImageOverlay";
import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { chatRouter, chatUtils } from "@editor/trpc/chat";

import CodeChangeBlock from "@/features/react-editor/components/chat/chat-elements/CodeChangeBlock";
import PageGenerationBlock from "@/features/react-editor/components/chat/chat-elements/PageGenerationBlock";
import SuggestedTemplates from "@/features/react-editor/components/chat/chat-elements/SuggestedTemplates";
import ToolBlock from "@/features/react-editor/components/chat/chat-elements/ToolBlock";
import {
  useMessagesForChat,
  useToolCallProgressForChat,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import ElementTypeTag from "@/features/react-editor/components/modifiers/ElementTypeTag";
import { useDefaultSelectedElementDesignation } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Expandable } from "@replo/design-system/components/expandable/Expandable";
import {
  Card,
  CardContent,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  CircleX,
  Globe,
  ImageIcon,
  Pencil,
  Puzzle,
  Search,
} from "lucide-react";
import { filterNulls } from "replo-utils/lib/array";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import {
  chatBuildErrorItemSchema,
  chatProductItemSchema,
  chatRevertItemSchema,
} from "schemas/chatMessage";
import { isChatTokenSegment, parseChatTokens } from "schemas/utils/chat";

import { useChatId } from "./hooks/useChatId";
import { MarkdownRenderer } from "./MarkdownRenderer";

interface Message {
  id: string;
  role: "user" | "assistant" | string;
  content: ChatMessageContent;
  createdAt?: Date;
  messageId: string;
}

interface ChatMessageProps extends Message {
  showTimeStamp?: boolean;
  animation?: Animation;
  className?: string;
  toolMessageId?: string;
}

type ToolCallMessageItem = Extract<
  ChatMessageContent[number],
  { type: "tool-call" }
>;

type TextMessageItem = Extract<ChatMessageContent[number], { type: "text" }>;

type ImageMessageItem = Extract<ChatMessageContent[number], { type: "image" }>;

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  messageId,
  toolMessageId,
}) => {
  const isUser = role === "user";

  if (role === "tool") {
    return null;
  }

  return (
    <div
      className={twMerge(
        "w-full flex flex-col gap-2",
        isUser ? "items-end" : "items-start",
      )}
    >
      <MessageContent
        content={content}
        role={role}
        messageId={messageId}
        toolMessageId={toolMessageId}
      />
    </div>
  );
};

const MessageContent: React.FC<{
  content: ChatMessageContent;
  role: string;
  messageId: string;
  toolMessageId?: string;
}> = ({ content, role, messageId, toolMessageId }) => {
  /**
   * NOTE (Kevin, 2025-08-11): We embed selected element context as a <chat-token> at the beginning of the
   * first text item when the user sends a message. We parse ONLY that first text
   * item to extract the token and show the pill above the bubble (not inline).
   * This keeps backend schemas untouched and avoids system-context injection;
   * the token is purely for frontend display of the selection context.
   */
  const isTextContent = (
    item: ChatMessageContent[number],
  ): item is Extract<ChatMessageContent[number], { type: "text" }> => {
    return item.type === "text";
  };

  const elementTypes = (() => {
    return content
      .filter(isTextContent)
      .map((item) => {
        const segments = parseChatTokens(item.text);
        return filterNulls(
          segments.filter(isChatTokenSegment).map((tokenSegment) => {
            if (tokenSegment.token.type === "selected-element") {
              return {
                elementType: tokenSegment.token.data.elementType,
                id: tokenSegment.id,
              };
            }
            return null;
          }),
        );
      })
      .flat();
  })();

  const images = content.filter(
    (item): item is ImageMessageItem => item.type === "image",
  );
  const nonImageContent = content.filter((item) => item.type !== "image");

  return (
    <div
      className={twMerge(
        "gap-0.5 w-full flex flex-col",
        role === "user" ? "items-end" : "items-start",
      )}
    >
      {elementTypes && (
        <div className="mb-1 inline-flex items-center gap-1">
          {elementTypes.map(({ elementType, id }) => (
            <ElementTypeTag key={id} elementType={elementType} />
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {images.map((item, index) => (
            <ImageMessageItem key={`image-${index}`} item={item} />
          ))}
        </div>
      )}

      {nonImageContent.map((item, index) => (
        <MessageItem
          key={`${item.type}-${index}`}
          item={item}
          role={role}
          messageId={messageId}
          toolMessageId={toolMessageId}
        />
      ))}
    </div>
  );
};

const MessageItem: React.FC<{
  item: ChatMessageContent[number];
  role: string;
  messageId: string;
  toolMessageId?: string;
}> = ({ item, role, messageId, toolMessageId }) => {
  const content = (() => {
    if (item.type === "text") {
      return <TextMessageItem item={item} role={role} messageId={messageId} />;
    }

    if (item.type === "tool-call") {
      return <ToolCallMessageItem item={item} toolMessageId={toolMessageId} />;
    }

    if (item.type === "image") {
      return <ImageMessageItem item={item} />;
    }

    return null;
  })();

  // Apply max-width constraint only to operational blocks (tool-calls), not text or images
  if (item.type === "image" || item.type === "text") {
    return content; // Images and text don't need max-width constraint
  }

  if (item.type === "tool-call" && item.toolName === "suggestTemplatesTool") {
    return content;
  }

  return <div className="max-w-[400px] w-full">{content}</div>;
};

// Text message item renderer
const TextMessageItem: React.FC<{
  item: TextMessageItem;
  role: string;
  messageId?: string;
}> = ({ item, role, messageId }) => {
  const isAssistant = role === "assistant";
  const {
    thinkBlocks,
    cleanedText,
    hasThinkBlocks,
    isStreamingThink,
    streamingThinkContent,
  } = extractThinkBlocks(item.text);

  const isThinkingDefaultOpen = isStreamingThink;
  const [isThinkingOpen, setIsThinkingOpen] = useState(isThinkingDefaultOpen);
  useOnValueChange(isStreamingThink, (currentValue, previousValue) => {
    if (previousValue && !currentValue) {
      setIsThinkingOpen(false);
    }
  });

  if (item.isHidden) {
    return null;
  }

  if (item.contentType === "product") {
    return <ProductRenderer text={item.text} />;
  }

  if (item.contentType === "buildError") {
    return <BuildErrorRenderer text={item.text} messageId={messageId} />;
  }

  if (item.contentType === "revert") {
    return <RevertRenderer text={item.text} />;
  }

  // Default text content (contentType === "text" or no contentType)
  if (item.contentType === "text" || !item.contentType) {
    const shouldShowThinkingBlock =
      isAssistant && (hasThinkBlocks || isStreamingThink);

    return (
      <div className="flex flex-col w-full">
        {shouldShowThinkingBlock && (
          <div className="w-full">
            <Expandable
              key={isStreamingThink ? "streaming" : "completed"} // Force re-render when state changes
              defaultOpen={isThinkingDefaultOpen}
              open={isThinkingOpen}
              onOpenChange={setIsThinkingOpen}
              trigger={
                <div className="h-[24px] flex flex-col justify-center items-center text-base text-muted hover:text-default transition-colors cursor-pointer ml-3">
                  <span>
                    {isThinkingOpen ? "Hide thinking" : "Show thinking"}
                  </span>
                </div>
              }
              content={
                <div className="border-l border-border pl-4 py-2 ml-3">
                  <div className="text-base text-muted/80 leading-relaxed italic">
                    {isStreamingThink ? (
                      <MarkdownRenderer>
                        {streamingThinkContent}
                      </MarkdownRenderer>
                    ) : (
                      thinkBlocks.map((block, index) => (
                        <div key={index} className="mb-2 last:mb-0">
                          <MarkdownRenderer>{block}</MarkdownRenderer>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              }
            />
          </div>
        )}

        <div
          className={twMerge(
            "text-base relative [word-break:break-word] px-3 py-2 rounded-lg text-default",
            role === "user" && "bg-surface ml-auto",
          )}
        >
          <MarkdownRenderer>
            {shouldShowThinkingBlock ? cleanedText : item.text}
          </MarkdownRenderer>
        </div>
      </div>
    );
  }

  return null;
};

// Tool call message item renderer
const ToolCallMessageItem: React.FC<{
  item: ToolCallMessageItem;
  toolMessageId?: string;
}> = ({ item, toolMessageId }) => {
  const chatId = useChatId();
  const allMessages = useMessagesForChat(chatId);
  const toolResult = findToolResult(item.toolCallId, allMessages);
  return (
    <div className="text-base pl-3 w-full min-w-0 my-2">
      <ToolCallRenderer
        contentItem={item}
        toolResult={toolResult}
        toolMessageId={toolMessageId}
      />
    </div>
  );
};

// Image message item renderer
const ImageMessageItem: React.FC<{
  item: ImageMessageItem;
}> = ({ item }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  if (item.isHidden) {
    return null;
  }

  return (
    <>
      <div
        key={item.id}
        className="w-[75px] h-[75px] overflow-hidden rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => setIsOverlayOpen(true)}
      >
        <img
          src={item.image}
          alt="Chat message image"
          className="w-full h-full object-cover"
        />
      </div>
      <ImageOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        imageSrc={item.image}
        imageAlt="Chat message image"
      />
    </>
  );
};

const ProductRenderer = ({ text }: { text: string }) => {
  const product = chatProductItemSchema.parse(JSON.parse(text));

  let featuredMediaUrl = product.featuredMedia?.url ?? "";
  if (product.media?.[0]?.type === "image") {
    featuredMediaUrl = product.media[0].url;
  }

  return (
    <div className="flex gap-3 border border-border rounded-lg p-1 w-[200px]">
      <div className="flex-shrink-0">
        {featuredMediaUrl ? (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
            <img
              src={featuredMediaUrl}
              alt={product.title}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
            <ImageIcon className="text-muted size-4" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-default truncate">
          {product.title}
        </div>
        <div className="text-xs text-muted mt-1 line-clamp-2">
          {product.description}
        </div>
      </div>
    </div>
  );
};

const BuildErrorRenderer = ({
  text,
  messageId,
}: {
  text: string;
  messageId?: string;
}) => {
  const { sendChatRequest, files } = useCurrentChatContext();
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const projectId = useCurrentProjectId();
  const chatId = useChatId();

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      void chatUtils.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const validatedText = chatBuildErrorItemSchema.safeParse(JSON.parse(text));
  if (!validatedText.success) {
    console.error("Invalid build error item", validatedText.error);
    return null;
  }

  const buildError = validatedText.data;

  const handleRetry = async () => {
    if (!chatId || !projectId || !messageId) {
      return;
    }

    const prompt = `Please retry the change. ${buildError.logs ? `Here are logs from the previous attempt that may be useful: ${buildError.logs}` : ""}`;
    await sendChatRequest({
      prompt,
      isHidden: true,
      defaultDesignation,
      filesToUpload: files,
    });
    updateMessage({
      id: messageId,
      projectId,
      content: [
        {
          type: "text",
          contentType: "buildError",
          text: JSON.stringify({
            ...buildError,
            hasRetried: true,
          }),
        },
      ],
    });
  };

  return (
    <div className="text-base w-full">
      <Card className="ml-3 w-auto p-0 mb-3 max-w-[388px]">
        <CardContent className="flex flex-col items-start gap-2 p-4">
          <div className="flex flex-row gap-3 items-start">
            <IconButton
              variant="secondary"
              UNSAFE_className="bg-danger-soft border-danger/50"
              icon={<CircleX size={16} className="text-danger" />}
            />
            <div className="flex flex-col gap-1">
              <div className="text-base font-semibold">
                We couldn't make that change
              </div>
              <div className="text-sm text-muted relative">
                Something went wrong while applying your edit, so we've safely
                reverted to the previous version.
              </div>
            </div>
          </div>
          <div className="flex w-full justify-end">
            <Button
              isLoading={buildError.isReverting}
              disabled={buildError.isReverting || buildError.hasRetried}
              variant="secondary"
              size="sm"
              onClick={() => {
                void handleRetry();
              }}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RevertRenderer = ({ text }: { text: string }) => {
  const parsedText = parseJsonOrNull(text);

  const validatedText = chatRevertItemSchema.safeParse(parsedText);
  if (!validatedText.success) {
    console.error("Invalid revert item", validatedText.error);
    return null;
  }

  return (
    <div className=" max-w-[400px] flex flex-col gap-2 pt-2 pl-3 w-full mb-3 mr-2">
      <div className="text-base text-muted hover:text-default transition-colors cursor-pointer">
        <span>Reverted to previous version</span>
      </div>
      <CodeChangeBlock
        isLoading={false}
        fileVersionId={validatedText.data.fileVersionId}
        appliedDiffs={[]}
        isCanceled={false}
      />
    </div>
  );
};

const ToolCallRenderer = ({
  contentItem,
  toolResult,
  toolMessageId,
}: {
  contentItem: ToolCallMessageItem;
  toolResult: ToolResultType | null;
  toolMessageId?: string;
}) => {
  const chatId = useChatId();
  const currentProgress = useToolCallProgressForChat(
    chatId,
    contentItem.toolCallId,
  );
  const isCanceled = toolResult?.output.value.isCanceled;

  if (!toolResult) {
    return exhaustiveSwitch({
      type: contentItem.toolName,
    })({
      smallCodeChangeTool: () => (
        <CodeChangeBlock
          isLoading
          isCanceled={isCanceled}
          progress={currentProgress}
        />
      ),
      mediumCodeChangeTool: () => (
        <CodeChangeBlock
          isLoading
          isCanceled={isCanceled}
          progress={currentProgress}
        />
      ),
      codeChangeSwarmTool: () => (
        <CodeChangeBlock
          isLoading
          isCanceled={isCanceled}
          progress={currentProgress}
          showCondensedProgress={false}
        />
      ),
      suggestTemplatesTool: () => (
        <SuggestedTemplates toolResult={null} toolMessageId={toolMessageId} />
      ),
      generatePageTool: () => (
        <PageGenerationBlock toolResult={null} toolMessageId={toolMessageId} />
      ),
      getIntegrationConfigTool: () => (
        <ToolBlock
          message="Fetching integration details..."
          icon={<Puzzle size={16} className="text-muted" />}
          isLoading
        />
      ),
      inputProcessorTool: () => (
        <ToolBlock
          message="Processing input..."
          icon={<Pencil size={16} className="text-muted" />}
          isLoading
        />
      ),
      assetSearchTool: () => (
        <ToolBlock
          message="Searching for assets..."
          icon={<Search size={16} className="text-muted" />}
          isLoading
        />
      ),
      URLTool: () => (
        <ToolBlock
          message="Visiting URL..."
          icon={<Globe size={16} className="text-muted" />}
          isLoading
        />
      ),
      getInspirationTool: () => (
        <ToolBlock
          message="Looking for reference images..."
          icon={<Search size={16} className="text-muted" />}
          isLoading
        />
      ),
    });
  }

  return exhaustiveSwitch(
    toolResult,
    "toolName",
  )({
    smallCodeChangeTool: (toolResult) => (
      <CodeChangeBlock
        isLoading={false}
        fileVersionId={toolResult.output.value.fileVersionId}
        appliedDiffs={toolResult.output.value.appliedDiffs}
        isCanceled={isCanceled}
        progress={currentProgress}
      />
    ),
    mediumCodeChangeTool: (toolResult) => (
      <CodeChangeBlock
        isLoading={false}
        fileVersionId={toolResult.output.value.fileVersionId}
        appliedDiffs={toolResult.output.value.appliedDiffs}
        isCanceled={isCanceled}
        progress={currentProgress}
      />
    ),
    codeChangeSwarmTool: (toolResult) => (
      <CodeChangeBlock
        isLoading={false}
        fileVersionId={toolResult.output.value.fileVersionId}
        appliedDiffs={toolResult.output.value.appliedDiffs}
        isCanceled={isCanceled}
        progress={currentProgress}
      />
    ),
    suggestTemplatesTool: (toolResult) => (
      <SuggestedTemplates
        toolResult={toolResult}
        toolMessageId={toolMessageId}
      />
    ),
    generatePageTool: (toolResult) => (
      <PageGenerationBlock
        toolResult={toolResult}
        toolMessageId={toolMessageId}
      />
    ),
    getIntegrationConfigTool: () => (
      <ToolBlock
        message="Fetched integration details"
        icon={<Puzzle size={16} className="text-muted" />}
      />
    ),
    inputProcessorTool: () => (
      <ToolBlock
        message="Processed input"
        icon={<Pencil size={16} className="text-muted" />}
      />
    ),
    assetSearchTool: () => (
      <ToolBlock
        message="Found assets"
        icon={<Search size={16} className="text-muted" />}
      />
    ),
    getInspirationTool: () => (
      <ToolBlock
        message="Found reference images"
        icon={<Search size={16} className="text-muted" />}
      />
    ),
    URLTool: () => (
      <ToolBlock
        message="Context updated"
        icon={<Search size={16} className="text-muted" />}
      />
    ),
  });
};

const findToolResult = (
  toolCallId: string,
  allMessages: ChatMessageType[],
): ToolResultType | null => {
  const toolResultMessage = allMessages.find((message) => {
    if (message.role !== "tool") {
      return false;
    }

    return message.content.some(
      (item) => item.type === "tool-result" && item.toolCallId === toolCallId,
    );
  });

  return toolResultMessage?.content.find(
    (item) => item.type === "tool-result" && item.toolCallId === toolCallId,
  ) as ToolResultType | null;
};

const extractThinkBlocks = (text: string) => {
  const thinkRegex = /<think>([\S\s]*?)<\/think>/g;
  const thinkBlocks: string[] = [];
  let match;

  // Check for streaming think block (opening tag without closing tag)
  const streamingThinkMatch = text.match(/^<think>([\S\s]*?)(?!<\/think>)$/);
  const isStreamingThink = streamingThinkMatch && !text.includes("</think>");

  if (isStreamingThink) {
    const streamingContent = streamingThinkMatch[1] ?? "";
    return {
      thinkBlocks: [],
      cleanedText: "",
      hasThinkBlocks: false,
      isStreamingThink: true,
      streamingThinkContent: streamingContent,
    };
  }

  // Extract completed think blocks
  match = thinkRegex.exec(text);
  while (match !== null) {
    thinkBlocks.push(match[1]?.trim() ?? "");
    match = thinkRegex.exec(text);
  }

  // Remove think blocks from the original text
  const cleanedText = text.replace(thinkRegex, "").trim();

  return {
    thinkBlocks,
    cleanedText,
    hasThinkBlocks: thinkBlocks.length > 0,
    isStreamingThink: false,
    streamingThinkContent: "",
  };
};
